import { useEffect, useState } from 'react';
import { api, ApiError, type Journal, type MediaCard } from './api.js';
import { href } from './router.js';

/** Fetches JSON for a URL; refetch() after a change. */
export function useApi<T>(url: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!url) return;
    let live = true;
    setError(null);
    api<T>('GET', url).then((d) => live && setData(d), (e: ApiError) => live && setError(e.message));
    return () => { live = false; };
  }, [url, tick]);
  return { data, error, refetch: () => setTick((t) => t + 1) };
}

export function Page({ title, back, children }: { title: string; back?: { href: string; label: string }; children: React.ReactNode }) {
  return (
    <main className="page">
      {back && <a className="back" href={back.href}>← {back.label}</a>}
      <h1>{title}</h1>
      {children}
    </main>
  );
}

export function Loading({ error }: { error?: string | null }) {
  return error ? <p className="error" role="alert">{error}</p> : <p className="loading">Loading…</p>;
}

const excerpt = (text: string, n = 40) => (text.length > n ? `${text.slice(0, n - 1)}…` : text);

/** A journal entry, verbatim. `onUnlink` adds a control that removes its link to the page's event. */
export function JournalQuote({ j, onUnlink }: { j: Journal; onUnlink?: () => void }) {
  return (
    <figure className={`journal ${j.kind === 'research' ? 'research' : ''}`}>
      <blockquote>{j.text}</blockquote>
      <figcaption>
        {j.kind === 'research' ? 'Research (unverified)' : 'Journal'} · {j.writtenAt.value?.slice(0, 16).replace('T', ' ') ?? j.writtenAt.raw ?? ''}
        {j.sources?.length ? <> · {j.sources.map((s, i) => s.url ? <a key={i} href={s.url} target="_blank" rel="noreferrer noopener">{s.title ?? 'source'}</a> : null)}</> : null}
        {onUnlink && <> · <button type="button" className="link" aria-label={`Unlink journal entry "${excerpt(j.text)}"`} onClick={onUnlink}>unlink</button></>}
      </figcaption>
    </figure>
  );
}

/** Thumbnails with a history link each; `onUnlink` adds a control that unlinks one from the page's event. */
export function MediaStrip({ exp, media, onUnlink }: { exp: string; media: MediaCard[]; onUnlink?: (m: MediaCard) => void }) {
  if (!media.length) return <p className="muted">No photos or clips.</p>;
  return (
    <ul className="strip">
      {media.map((m) => (
        <li key={m.id}>
          <div className="thumb">
            {m.hasPreview ? (
              <a href={m.previewUrl} target="_blank" rel="noreferrer noopener">
                <img src={m.thumbUrl} alt={m.id} loading="lazy" />
              </a>
            ) : (
              <div className="placeholder" title={m.file} aria-label={`${m.id}: ${m.storage === 'mac' ? 'stored on the Mac, no preview on the server yet' : 'no preview yet'}`}>
                <span aria-hidden="true">{m.kind === 'video' ? '🎞' : '📷'}</span>
                <small>{m.storage === 'mac' ? 'on Mac' : 'no preview'}</small>
              </div>
            )}
            <span className="tag">{m.kind === 'video' ? '▶ ' : ''}{m.coverageRole ?? m.id}</span>
            {m.quality?.length ? <span className="warn" title={m.quality.join(', ')}>⚠</span> : null}
          </div>
          <div className="tools">
            <a href={href.history(exp, 'media', m.id)} aria-label={`History of ${m.id}`}>history</a>
            {onUnlink && <button type="button" className="link" aria-label={`Unlink ${m.id}`} onClick={() => onUnlink(m)}>unlink</button>}
          </div>
        </li>
      ))}
    </ul>
  );
}
